import React, { useState } from 'react'
import { useVideo } from '../context/VideoContext'
import { Upload, X, CheckCircle } from 'lucide-react'
import toast from 'react-hot-toast'
import { uploadRouter } from '../services/UploadRouter'
import { isValidVideoFile, isFileSizeValid, formatFileSize, VIDEO_FORMATS, UPLOAD_CONFIG } from '../utils/uploadConstants'
import { sanitizeVideoFilename } from '../utils/videoFileValidation'
import { Link } from 'react-router-dom'

// 🚀 ENTERPRISE: Video accept attribute for file input
const VIDEO_ACCEPT_ATTRIBUTE = VIDEO_FORMATS.ALL_SUPPORTED.map(ext =>
  ext.replace('.', '')
).join(',')

// 🚀 ENTERPRISE: Unified validation function
const validateVideoFile = (file) => {
  if (!isValidVideoFile(file)) {
    return {
      isValid: false,
      error: `Invalid file type. Supported formats: ${VIDEO_FORMATS.ALL_SUPPORTED.join(', ')}`
    }
  }

  if (!isFileSizeValid(file.size)) {
    return {
      isValid: false,
      error: `File size exceeds ${formatFileSize(UPLOAD_CONFIG.MAX_FILE_SIZE_BYTES)} limit`
    }
  }

  return { isValid: true }
}

const UploadPage = () => {
  const { uploadVideo, uploadProgress, loading, cancelUpload } = useVideo()

  // 🚀 ENTERPRISE: Get upload status from UploadRouter for cancel button
  const uploadStatus = uploadRouter.getStatus()

  // 🚀 ENTERPRISE: Debug logging for upload status
  React.useEffect(() => {
    console.log('📡 UploadPage: uploadStatus:', uploadStatus)
    console.log('📡 UploadPage: uploadProgress:', uploadProgress)
    console.log('📡 UploadPage: canCancel:', uploadStatus.canCancel)
  }, [uploadStatus, uploadProgress])
  
  // Check authentication via localStorage (simple token system)
  const token = localStorage.getItem('token')
  const hasPermission = () => !!token
  
  const [dragActive, setDragActive] = useState(false)
  const [selectedFile, setSelectedFile] = useState(null)
  const [metadata, setMetadata] = useState({
    title: '',
    description: '',
    category: 'Family Events',
    tags: '',
    visibility: 'private'
  })
  const [uploadSuccess, setUploadSuccess] = useState(false)

  // Categories for videos
  const categories = [
    'Family Events',
    'Holidays',
    'Birthdays', 
    'Vacations',
    'Kids',
    'Pets',
    'Home Videos',
    'Special Occasions',
    'Other'
  ]

  const visibilityOptions = [
    { value: 'private', label: 'Private (Family Only)' },
    { value: 'restricted', label: 'Restricted (Selected Users)' },
    { value: 'unlisted', label: 'Unlisted (Link Only)' }
  ]

  const handleDrag = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }

  const handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)
    
    const files = e.dataTransfer.files
    if (files && files[0]) {
      handleFileSelect(files[0])
    }
  }

  const handleFileSelect = async (file) => {
    if (!file) return

    console.log('🔍 UploadPage file selected:', {
      name: file.name,
      type: file.type,
      size: file.size,
      extension: file.name.toLowerCase().substring(file.name.lastIndexOf('.'))
    })

    // Validate video file type and size
    const validation = validateVideoFile(file)
    if (!validation.isValid) {
      toast.error(validation.error)
      return
    }

    // Sanitize filename (remove apostrophes and special chars)
    const sanitizedName = sanitizeVideoFilename(file.name)
    let processedFile = file

    if (sanitizedName !== file.name) {
      console.log('🧹 UploadPage sanitizing filename:', file.name, '→', sanitizedName)
      processedFile = new File([file], sanitizedName, { type: file.type })
    }

    setSelectedFile(processedFile)

    // Auto-populate title from filename
    if (!metadata.title) {
      const nameWithoutExt = processedFile.name.replace(/\.[^/.]+$/, '')
      setMetadata(prev => ({
        ...prev,
        title: nameWithoutExt
      }))
    }

    setUploadSuccess(false)
  }



  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!selectedFile) {
      toast.error('Please select a video file')
      return
    }

    if (!metadata.title.trim()) {
      toast.error('Please enter a title')
      return
    }

    const tags = metadata.tags.split(',').map(tag => tag.trim()).filter(tag => tag.length > 0)
    
    const result = await uploadVideo(selectedFile, {
      ...metadata,
      tags
    })

    if (result.success) {
      setUploadSuccess(true)
      setSelectedFile(null)
      setMetadata({
        title: '',
        description: '',
        category: 'Family Events',
        tags: '',
        visibility: 'private'
      })
      
      // Redirect to success page after 2 seconds to show videos
      setTimeout(() => {
        window.location.href = '/success'
      }, 2000)
    }
  }

  const removeFile = () => {
    setSelectedFile(null)
    setUploadSuccess(false)
  }


  const formatFileSize = (bytes) => {
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    if (bytes === 0) return '0 Bytes'
    const i = Math.floor(Math.log(bytes) / Math.log(1024))
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i]
  }

  if (!hasPermission('uploader')) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-4">Access Denied</h1>
          <p className="text-gray-400">You don't have permission to upload videos.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Upload Video</h1>
          <p className="text-gray-400">Share your family memories with loved ones</p>
        </div>

        {uploadSuccess && (
          <div className="bg-green-900 border border-green-600 rounded-lg p-4 mb-6 text-center">
            <CheckCircle className="w-8 h-8 text-green-400 mx-auto mb-2" />
            <h3 className="text-green-400 font-semibold mb-1">Upload Successful!</h3>
            <p className="text-green-300 text-sm">Your video has been uploaded and is being processed.</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* File Upload Area */}
          <div
            className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              dragActive 
                ? 'border-netflix-red bg-netflix-red bg-opacity-10' 
                : 'border-gray-600 hover:border-gray-500'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <input
              type="file"
              accept={VIDEO_ACCEPT_ATTRIBUTE}
              onChange={(e) => handleFileSelect(e.target.files[0])}
              disabled={loading}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            />
            
            {selectedFile ? (
              <div className="space-y-4">
                <div className="text-green-400 mb-4">
                  <CheckCircle className="w-12 h-12 mx-auto mb-2" />
                  <p className="font-semibold">File Selected</p>
                </div>
                
                <div className="bg-gray-800 rounded-lg p-4 text-left">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-white font-medium">{selectedFile.name}</p>
                      <p className="text-gray-400 text-sm">{formatFileSize(selectedFile.size)}</p>
                    </div>
                    <button
                      type="button"
                      onClick={removeFile}
                      className="text-gray-400 hover:text-white transition-colors"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                  
                  {uploadProgress > 0 && (
                    <div className="mt-4">
                      <div className="flex justify-between text-sm text-gray-400 mb-1">
                        <span>Uploading...</span>
                        <div className="flex items-center space-x-2">
                          <span>{uploadProgress}%</span>
                          {uploadStatus.canCancel && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.preventDefault()
                                e.stopPropagation()
                                console.log('🚨 ENTERPRISE: Cancel button clicked - using VideoContext.cancelUpload()')
                                // 🚀 ENTERPRISE: Use VideoContext cancel (which delegates to UploadRouter)
                                cancelUpload()
                              }}
                              className="relative z-10 text-xs bg-gray-600 hover:bg-gray-500 text-white px-2 py-1 rounded transition-colors"
                              title="Cancel upload"
                            >
                              Cancel
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="w-full bg-gray-700 rounded-full h-2">
                        <div
                          className="bg-netflix-red h-2 rounded-full transition-all duration-300"
                          style={{ width: `${uploadProgress}%` }}
                        />
                      </div>
                      {uploadStatus.isActive && (
                        <p className="text-xs text-gray-500 text-center mt-1">
                          {uploadStatus.method === 'multipart' ? 'Multipart upload in progress...' : 'Upload in progress...'} You can cancel anytime.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <Upload className="w-12 h-12 text-gray-400 mx-auto" />
                <div>
                  <p className="text-white text-lg font-medium mb-2">
                    Drag and drop your video file here
                  </p>
                  <p className="text-gray-400 text-sm mb-4">
                    or click to browse files
                  </p>
                  <p className="text-gray-500 text-xs">
                    Supported formats: MP4, AVI, MOV, WMV, WebM, MKV (Max 10GB)
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Video Metadata */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Title *
              </label>
              <input
                type="text"
                value={metadata.title}
                onChange={(e) => setMetadata({...metadata, title: e.target.value})}
                className="input-field"
                placeholder="Enter video title"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Description
              </label>
              <textarea
                value={metadata.description}
                onChange={(e) => setMetadata({...metadata, description: e.target.value})}
                className="input-field h-32 resize-none"
                placeholder="Describe your video (optional)"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Category
                </label>
                <select
                  value={metadata.category}
                  onChange={(e) => setMetadata({...metadata, category: e.target.value})}
                  className="input-field"
                >
                  {categories.map(category => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Visibility
                </label>
                <select
                  value={metadata.visibility}
                  onChange={(e) => setMetadata({...metadata, visibility: e.target.value})}
                  className="input-field"
                >
                  {visibilityOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Tags
              </label>
              <input
                type="text"
                value={metadata.tags}
                onChange={(e) => setMetadata({...metadata, tags: e.target.value})}
                className="input-field"
                placeholder="Enter tags separated by commas (optional)"
              />
              <p className="text-gray-500 text-xs mt-1">
                Example: vacation, beach, summer, family
              </p>
            </div>
          </div>

          {/* Video Playback Help */}
          <div className="bg-orange-600/10 border border-orange-600/20 rounded-lg p-4 mb-4">
            <div className="flex items-center space-x-2 mb-2">
              <span className="text-2xl">📞</span>
              <h3 className="text-orange-200 font-semibold">Video won't play after uploading?</h3>
            </div>
            <p className="text-orange-300/80 text-sm mb-2">
              Some video formats may need conversion for web browsers (including .mov, .avi, .mkv, H.265, and others)
            </p>
            <p className="text-orange-200 text-sm font-medium">
              Contact admin for help with video conversion - we can quickly convert your video to a web-compatible format!
            </p>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={!selectedFile || loading || uploadProgress > 0}
            className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed py-3"
          >
            {loading || uploadProgress > 0 ? 'Uploading...' : 'Upload Video'}
          </button>
        </form>
      </div>
    </div>
  )
}

export default UploadPage